'use client';

import { useUnmount } from 'ahooks';
import FixedChatWindow from '../../message/FixedChatWindow';
import PythraSummary from '../../ui/PythraSummary';
import { useHeader } from '@/contexts/HeaderContext';
import MarketHeader from './MarketHeader';
import MarketOverview from './MarketOverview';
import { Skeleton } from '@mui/material';
import useMarketEvent from '../../hooks/use-market-event';
import MarketChart from './MarketChart';
import MarketDrawerRender from './MarketDrawerRender';
import HoldingPositions from './HoldingPositions';
import useFilteredMarkets from '@/hooks/use-filtered-markets';
import { isRecurringCrypto } from './crypto-utils';
import CryptoTrendChart from '../../chart/CryptoTrendChart';
import CryptoMultipleTrendChart from '../../chart/CryptoMultipleTrendChart';
import MarketPrice from './MarketPrice';
import MarketTraders from './MarketTraders';
import Empty from '../../ui/Empty';

interface MarketDetailProps {
  id?: string;
  openInSidebar?: boolean;
}
export default function MarketDetail({ id, openInSidebar }: MarketDetailProps) {
  const { setRightContent, setIsTransparent } = useHeader();

  const { data: eventResponse, isLoading } = useMarketEvent({ eventSlug: id, enabled: !!id });
  const eventData = eventResponse?.data;
  const filteredMarkets = useFilteredMarkets({
    markets: eventData?.markets,
    isClosed: eventData?.event.closed === 1,
    sortBy: eventData?.event.sort_by,
  });
  const isRecurring = isRecurringCrypto(eventData?.tags || []);
  const cryptoMarketItemsCount = eventData?.markets?.length;

  useUnmount(() => {
    setRightContent(null);
    setIsTransparent(true);
  });

  // 直接看 isLoading 就够：SWR 的 isLoading 仅在「没有缓存数据且有请求在飞」时为真，
  // 重新校验（已有数据）时是 false，所以不会出现切回来时闪骨架。
  // 早先这里加了个 useMount 置位的 mountedRef 做二次限制，但它在首次 commit 之后
  // 立刻变 true——那时数据还没回来，中途任何一次重渲染都会把骨架撤掉，
  // 然后因为 eventData 仍是 undefined 一路走到末尾 return null，整页白屏。
  if (isLoading) {
    return (
      <div className="max-w-245 mx-auto flex flex-col gap-3 mb-6">
        <Skeleton variant="rounded" width="100%" height={30} sx={{ bgcolor: 'grey.800' }} />
        <Skeleton variant="rounded" width="90%" height={30} sx={{ bgcolor: 'grey.800' }} />
        <Skeleton variant="rounded" width="70%" height={30} sx={{ bgcolor: 'grey.800' }} />
      </div>
    );
  }

  if (eventData) {
    let chart = (
      <MarketChart eventData={eventData} markets={filteredMarkets} openInSidebar={openInSidebar} />
    );

    if (isRecurring) {
      chart =
        cryptoMarketItemsCount === 1 ? (
          <CryptoTrendChart eventData={eventData} />
        ) : (
          <CryptoMultipleTrendChart eventData={eventData} />
        );
    }

    return (
      <>
        <div className="max-w-245 mx-auto pb-30">
          <MarketHeader
            title={eventData.event.title}
            marketEventId={eventData.event.id}
            polymarketUrl={eventData.event.url || ''}
          />
          <PythraSummary summary={eventData?.event?.summary || eventData?.event?.description} />
          <MarketOverview eventData={eventData} />
          {chart}
          <HoldingPositions
            eventId={eventData.event.id}
            markets={filteredMarkets}
            isClosed={eventData.event.closed === 1}
          />
          <MarketPrice markets={filteredMarkets} />
          <MarketTraders eventSlug={eventData.event.slug || id || ''} markets={filteredMarkets} />
        </div>
        {!openInSidebar && <MarketDrawerRender marketEventId={eventData?.event?.id} />}
        {!isLoading && !openInSidebar && (
          <FixedChatWindow
            suggestions={eventData?.event?.recommended_questions || []}
            sourceId={`market_${eventData?.event?.slug}`}
            userContext={
              eventData
                ? JSON.stringify({
                    context_type: 'market',
                    data: eventData,
                  })
                : undefined
            }
          />
        )}
      </>
    );
  }

  // 走到这里说明：没传 id、请求失败、或接口返回了空数据。
  // 原来直接 return null，用户看到的是一片白，分不清是加载中还是出错了。
  return <Empty className="py-30" />;
}
